import React, {useContext, useState} from 'react';
import {Card} from "../../../../../../components/card/card.jsx";
import Button from "../../../../../../components/button/button.jsx";
import {FaEye, FaPlus} from "react-icons/fa";
import {IndividualContext} from "../../individual-context.jsx";
import {v4 as uuidv4} from "uuid";
import {AgGridReact} from "ag-grid-react";
import aggridUtils from "../../../../../../utils/aggrid-utils.js";
import projectUtils from "../../../../../../utils/project-utils.js";
import GenericEditRemoveCellRenderer
    from "../../../../agents/form/components/tabs/generic-edit-remove-cell-renderer.jsx";
import ConfirmationDeleteList from "../../../../../../components/utils/confirmation-delete-lists.jsx";
import {Modal} from "../../../../../../components/modal/modal.jsx";
import SentryRow from "../../../../../../components/template/sentry-row.jsx";
import InputForm from "../../../../../../components/input/input-form.jsx";
import InputDatetime from "../../../../../../components/input/input-datetime.jsx";
import useFetchData from "../../hooks/use-fetch-data.js";
import SelectComponent from "../../../../../../components/select/select-component.jsx";
import {format} from "date-fns";
import Attach from "../../../../../../components/attach/attach.jsx";
import AttachShow from "../../../../../../components/attach/attach-show.jsx";

const ArticleAttachCellRenderer = ({data}) => {
    const [openModal, setOpenModal] = useState(false);
    const {attachId, articleFile} = data;

    return (
        <>
            <div className={"w-full flex justify-center items-center h-full"}>
                {
                    (attachId || articleFile) &&
                    <Button className={"!w-[30px] !h-[25px] !p-0"} title={"Visualizar anexo"} onClick={() => setOpenModal(true)}>
                        <FaEye />
                    </Button>
                }
            </div>

            <AttachShow
                open={openModal}
                setOpen={setOpenModal}
                attachId={attachId}
                newOpen={!!articleFile}
                newFileShow={articleFile}
                showRemove={false}
                modalTitle={"Anexo do artigo"}
            />
        </>
    );
}

const initialData = () => ({
    itemId: uuidv4(),
    bo: "",
    date: "",
    article: "",
    attachId: "",
    articleFile: null
});

const Articles = () => {
    const {individual, setIndividual, setChanges} = useContext(IndividualContext)
    const [openModal, setOpenModal] = useState(false)
    const [data, setData] = useState(initialData)
    const {systemNatures} = useFetchData()

    const isFutureDate = (date) => {
        if (!date) return false;

        return date > format(new Date(), "yyyy-MM-dd");
    }

    const onChangeCustom = (value, id) => setData(prev => ({...prev, [id]: value}))
    const onChange = ({target: {id, value}}) => setData(prev => ({...prev, [id]: value}));
    const onChangeFile = (file) => {
        if (file instanceof File) setData(prev => ({...prev, articleFile: file}));
    }
    const removeFile = () => setData(prev => ({...prev, attachId: null, articleFile: null}));

    const remove = (data) => {
        ConfirmationDeleteList(() => {
            setIndividual(prev => ({
                ...prev,
                articles: prev.articles.filter(dep => dep.itemId !== data.itemId)
            }));

            setChanges(prev => (prev.includes("articles") ? prev : [...prev, "articles"]))
        })
    }

    const [columnDefs] = useState([
        { headerName: "Data", field: "date", comparator: aggridUtils.nullLastComparator, width: 90,
            valueFormatter: ({value}) => {
                return projectUtils.formatDate(value, "DD/MM/YYYY")
            }
        },
        { headerName: "Boletim", field: "bo", comparator: aggridUtils.nullLastComparator },
        { headerName: "Natureza", field: "article", flex: 1, comparator: aggridUtils.nullLastComparator },
        {
            headerName: "Anexo", field: "", width: 60,
            cellRenderer: ArticleAttachCellRenderer
        },
        {
            headerName: "", field: "", width: 90,
            cellRenderer: GenericEditRemoveCellRenderer,
            cellRendererParams: {setData, setOpen: setOpenModal, remove}
        },
    ]);

    const save = () => {
        if (!data.article) {
            projectUtils.swalErrorMessage("", "O campo \"Natureza\" é obrigatório.", "Ok");
            return
        }

        if (isFutureDate(data.date)) {
            projectUtils.swalErrorMessage("", "A data não pode ser maior que a data de hoje.", "Ok");
            return
        }

        setIndividual(prevItems => {
            const exists = prevItems.articles.some(t => t.itemId === data.itemId);

            const updated = exists
                ? prevItems.articles.map(t => (t.itemId === data.itemId ? data : t))
                : [...prevItems.articles, data];

            return {
                ...prevItems,
                articles: updated
            }
        });

        setChanges(prev => (prev.includes("articles") ? prev : [...prev, "articles"]))
        onHide()
    }

    const onHide = () => {
        setData(initialData);
        setOpenModal(false)
    }

    return (
        <>
            <Card>
                <Card.Header className={"gap-2"}>
                    <Card.Title>Artigos indiciados</Card.Title>
                    <Button className="h-7.5! w-7.5 p-0!" onClick={() => setOpenModal(true)}>
                        <FaPlus/>
                    </Button>
                </Card.Header>
                <Card.Body>

                    <div className={"h-[calc(100vh-195px)]"}>
                        <AgGridReact
                            rowData={individual.articles}
                            columnDefs={columnDefs}
                            animateRows={false}
                            suppressCellFocus={true}
                            gridOptions={{localeText: aggridUtils.localization()}}
                            theme={aggridUtils.generalTheme()}
                        />
                    </div>
                </Card.Body>
            </Card>

            <Modal overflow={true} isCloseClick={false} open={openModal} setOpen={onHide}  title={"Artigo"} className={"w-3/12"}>
                <Modal.Body>
                    <SentryRow>
                        <div className="col-span-12">
                            <InputDatetime
                                id="date"
                                label="Data"
                                selected={data.date}
                                onChange={(date) => {
                                    const datePrepared = date ? format(date, "yyyy-MM-dd") : ""
                                    onChangeCustom(datePrepared, "date")
                                }}
                                classNameWrapper={"w-full"}
                                dateFormat="L"
                            />
                        </div>
                        <div className="col-span-12">
                            <InputForm
                                id="bo"
                                value={data.bo}
                                onChange={onChange}
                                label="Boletim"
                            />
                        </div>
                        <div className="col-span-12">
                            <SelectComponent
                                id={"article"}
                                value={data.article}
                                options={systemNatures}
                                label={"Natureza *"}
                                onChange={(value) => onChangeCustom(value?.value, "article")}
                            />
                        </div>
                        <div className="col-span-4">
                            <Attach
                                onChangeFile={onChangeFile}
                                removeFile={removeFile}
                                itemId={`articles-${data.itemId}`}
                                attachedFile={data.articleFile}
                                attachId={data.attachId}
                                modalTitle={"Anexo do artigo"}
                            />
                        </div>
                    </SentryRow>
                </Modal.Body>

                <Modal.Footer className={"p-1"}>
                    <div className={"w-full flex justify-end"}>
                        <Button onClick={save}>
                            Confirmar
                        </Button>
                    </div>
                </Modal.Footer>
            </Modal>
        </>
    );
};

export default Articles;
